/**
 * Regras do Portal do Cliente que não dependem de tela: como uma validade vira
 * cor e como várias certidões viram o semáforo da empresa.
 *
 * Ficam separadas das telas porque valem igual para o dado fictício de hoje e
 * para o dado real da fase 2 — e porque é aqui que um erro de dia faria o
 * cliente ver "em dia" com certidão vencida.
 */
import { differenceInCalendarDays, parseISO } from 'date-fns';

/** A partir de quantos dias para o vencimento o item passa a pedir atenção. */
export const DIAS_DE_ATENCAO = 30;

/**
 * O Certificado de Regularidade do FGTS vale só 30 dias e é reemitido em ciclo:
 * com a regra geral ele ficaria amarelo o tempo todo, e o amarelo deixaria de
 * dizer alguma coisa.
 */
export const DIAS_DE_ATENCAO_FGTS = 7;

/**
 * `sem_informacao` só vale para a empresa inteira: nada registrado para avaliar.
 * Melhor dizer isso do que afirmar "em dia" sem nenhuma certidão por trás.
 */
export type Farol = 'em_dia' | 'atencao' | 'pendencia' | 'sem_informacao';

/** Dias até a data (negativo quando já passou). Datas em `AAAA-MM-DD`. */
export function diasAte(data: string, hoje: Date): number {
  return differenceInCalendarDays(parseISO(data), hoje);
}

/** Validade de um documento: vencida é pendência; perto de vencer, atenção. */
export function farolDaValidade(validaAte: string | null, hoje: Date, diasDeAtencao = DIAS_DE_ATENCAO): Farol {
  if (!validaAte) return 'pendencia';
  const dias = diasAte(validaAte, hoje);
  if (dias < 0) return 'pendencia';
  if (dias <= diasDeAtencao) return 'atencao';
  return 'em_dia';
}

/** O pior farol vence: uma pendência basta para a empresa não estar em dia. */
export function piorFarol(farois: Farol[]): Farol {
  if (farois.includes('pendencia')) return 'pendencia';
  if (farois.includes('atencao')) return 'atencao';
  return 'em_dia';
}

/** Frase curta da validade, na linguagem do cliente. */
export function textoDaValidade(validaAte: string | null, hoje: Date): string {
  if (!validaAte) return 'Sem certidão válida';
  const dias = diasAte(validaAte, hoje);
  if (dias < 0) return dias === -1 ? 'Venceu ontem' : `Venceu há ${-dias} dias`;
  if (dias === 0) return 'Vence hoje';
  if (dias === 1) return 'Vence amanhã';
  return `Vence em ${dias} dias`;
}

export const ROTULO_DO_FAROL: Record<Farol, string> = {
  em_dia: 'Em dia',
  atencao: 'Atenção',
  pendencia: 'Pendência',
  sem_informacao: 'Sem informação',
};

export const VARIANTE_DO_FAROL: Record<Farol, 'sucesso' | 'alerta' | 'erro' | 'outline'> = {
  em_dia: 'sucesso',
  atencao: 'alerta',
  pendencia: 'erro',
  sem_informacao: 'outline',
};

type CertidaoParaFarol = { tipo: string; validaAte: string | null; acompanhada: boolean; classificacao?: string | null };

/**
 * Farol de uma certidão; `null` para a que o portal ainda não acompanha.
 *
 * A validade vem primeiro (vencida é pendência mesmo classificada como regular
 * ontem); depois, a classificação do GOB: irregular ou sem certidão também é
 * pendência. Mesma ordem do `estadoCertidao` do Raio X.
 */
export function farolDaCertidao(c: CertidaoParaFarol, hoje: Date): Farol | null {
  if (!c.acompanhada) return null;
  const pelaValidade = farolDaValidade(c.validaAte, hoje, c.tipo === 'fgts' ? DIAS_DE_ATENCAO_FGTS : DIAS_DE_ATENCAO);
  if (pelaValidade === 'pendencia') return 'pendencia';
  if (c.classificacao === 'irregular' || c.classificacao === 'sem_certidao') return 'pendencia';
  return pelaValidade;
}

export interface ResumoRegularidade {
  farol: Farol;
  /** O que puxou o farol para baixo, em frases curtas. Vazio quando está em dia. */
  motivos: string[];
}

type DadosParaResumo = {
  certidoes: (CertidaoParaFarol & { empresaId: string; nome: string })[];
  certificados: { empresaId: string; venceEm: string }[];
  alvaras: { empresaId: string; status: string }[];
};

/**
 * Semáforo de uma empresa: certidões, certificado digital e alvará. Situação
 * fiscal com parcelamento em dia não entra — ela não impede a certidão, e
 * acender o amarelo por ela assustaria sem pedir ação.
 */
export function resumoDaEmpresa(dados: DadosParaResumo, empresaId: string, hoje: Date): ResumoRegularidade {
  const motivos: string[] = [];
  const farois: Farol[] = [];
  let certidoesAvaliadas = 0;

  for (const c of dados.certidoes.filter(x => x.empresaId === empresaId)) {
    const f = farolDaCertidao(c, hoje);
    if (!f) continue;
    certidoesAvaliadas += 1;
    farois.push(f);
    if (f !== 'em_dia') motivos.push(`${c.nome}: ${textoDaValidade(c.validaAte, hoje).toLowerCase()}`);
  }

  const cert = dados.certificados.find(x => x.empresaId === empresaId);
  if (cert) {
    const f = farolDaValidade(cert.venceEm, hoje);
    farois.push(f);
    if (f !== 'em_dia') motivos.push(`Certificado digital: ${textoDaValidade(cert.venceEm, hoje).toLowerCase()}`);
  }

  const alvara = dados.alvaras.find(x => x.empresaId === empresaId);
  if (alvara?.status === 'pendente_cliente') {
    farois.push('atencao');
    motivos.push('Alvará: aguardando informação sua');
  }

  // Sem nenhuma certidão registrada não há base para "em dia": o farol diz que
  // falta informação, a não ser que outro item já peça atenção.
  if (certidoesAvaliadas === 0) {
    motivos.unshift('Nenhuma certidão registrada pelo escritório');
    const pior = piorFarol(farois);
    return { farol: pior === 'em_dia' ? 'sem_informacao' : pior, motivos };
  }

  return { farol: piorFarol(farois), motivos };
}

/**
 * Diz se o caminho é do Portal do Cliente. Sem distinguir maiúsculas, porque o
 * React Router também não distingue: `/PORTAL-CLIENTE` abre a mesma tela, e uma
 * guarda que comparasse letra a letra deixaria passar quem não é admin.
 */
export function ehRotaDoPortalCliente(pathname: string): boolean {
  const p = pathname.toLowerCase();
  return p === '/portal-cliente' || p.startsWith('/portal-cliente/');
}
